import { Mail, MessageSquareText, Plus, Search } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth";
import { useTenants } from "@/lib/queries";
import { LANGUAGE_NAME, channelOf, languagesOf, type TemplateChannel, type TextTemplate } from "@/lib/text-templates";
import { useTextTemplates } from "@/lib/text-templates-api";
import { formatDateTime, relativeTime } from "@/lib/utils";

/**
 * Text templates (ACL-385, ADR-111 in platform): the words a workflow texts a lead, each written
 * once in English and, when wanted, in Spanish. The list says which languages each has and which
 * workflows send it; a template is opened to edit it, with a preview of the text as a lead gets it.
 *
 * Admins only, as core-api's routes and the workflows are. Nothing is deleted: a template is turned
 * off, and core-api refuses that while a published workflow sends it.
 *
 * The same list is the Email templates page (ACL-378): the emails a workflow's Send email step
 * sends, each with its subject. `channel` says which; core-api's one list holds both.
 */
const WORDS: Record<
  TemplateChannel,
  { title: string; description: string; path: string; empty: string; emptyText: string; failed: string }
> = {
  sms: {
    title: "Text templates",
    description: "The texts a workflow sends a lead, written once in English and Spanish.",
    path: "/text-templates",
    empty: "No text templates yet",
    emptyText: "Write a text once, in English and Spanish, and pick it in a workflow's Send SMS step.",
    failed: "Could not load the text templates",
  },
  email: {
    title: "Email templates",
    description: "The emails a workflow sends a lead, each with its subject, written once in English and Spanish.",
    path: "/email-templates",
    empty: "No email templates yet",
    emptyText: "Write an email once, in English and Spanish, and pick it in a workflow's Send email step.",
    failed: "Could not load the email templates",
  },
};

export function TextTemplatesPage({ channel = "sms" }: { channel?: TemplateChannel }) {
  const words = WORDS[channel];
  const Icon = channel === "email" ? Mail : MessageSquareText;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const tenants = useTenants({ enabled: isSuperadmin });
  const pickTenant = isSuperadmin && (tenants.data?.length ?? 0) > 1;
  const [picked, setPicked] = React.useState<number>();
  const tenantId = pickTenant ? (picked ?? tenants.data?.[0]?.id) : undefined;
  const { data, isPending, error, refetch } = useTextTemplates(tenantId, !isSuperadmin || !tenants.isPending);
  const [query, setQuery] = React.useState("");

  const all = (data ?? []).filter((t) => channelOf(t) === channel);
  const needle = query.trim().toLowerCase();
  const shown = all.filter(
    (t) =>
      !needle ||
      t.name.toLowerCase().includes(needle) ||
      Object.values(t.texts).some((x) => x?.toLowerCase().includes(needle)) ||
      Object.values(t.subjects ?? {}).some((x) => x?.toLowerCase().includes(needle)),
  );
  const open = (t: TextTemplate) => navigate(`${words.path}/${encodeURIComponent(t.slug)}${tenantId ? `?tenant=${tenantId}` : ""}`);

  return (
    <Page>
      <PageHeader
        title={words.title}
        description={words.description}
        action={
          <div className="flex items-center gap-2">
            {pickTenant && (
              <Select value={String(tenantId)} onValueChange={(value) => setPicked(Number(value))}>
                <SelectTrigger aria-label="Tenant" className="w-52">
                  <SelectValue placeholder="Choose tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.data?.map((tenant) => (
                    <SelectItem key={tenant.id} value={String(tenant.id)}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="default" asChild>
              <Link to={`${words.path}/new${tenantId ? `?tenant=${tenantId}` : ""}`}>
                <Plus />
                New template
              </Link>
            </Button>
          </div>
        }
      />

      {all.length > 0 && (
        <div className="relative mb-3 w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-subtle" aria-hidden />
          <Input
            aria-label="Search templates"
            placeholder="Search templates"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      )}

      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title={words.failed}
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : all.length === 0 ? (
          <EmptyState
            icon={Icon}
            title={words.empty}
            description={words.emptyText}
            action={
              <Button variant="default" asChild>
                <Link to={`${words.path}/new${tenantId ? `?tenant=${tenantId}` : ""}`}>
                  <Plus />
                  New template
                </Link>
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState icon={Search} title="No template found" description="Try another name or word." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Languages</TableHead>
                <TableHead>Used by</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((t) => (
                <TableRow
                  key={t.slug}
                  className="cursor-pointer [&>td]:h-14"
                  tabIndex={0}
                  role="link"
                  aria-label={`Open ${t.name}`}
                  onClick={() => open(t)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") open(t);
                  }}
                >
                  <TableCell>
                    <p className="font-medium text-text">{t.name}</p>
                    <p className="max-w-md truncate text-xs text-text-muted">
                      {channel === "email" ? (t.subjects?.en ?? "") : t.texts.en}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {languagesOf(t).map((l) => (
                        <Badge key={l} tone="neutral" size="sm">
                          {LANGUAGE_NAME[l]}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {t.used_by.length === 0
                      ? "No workflow yet"
                      : t.used_by.length === 1
                        ? t.used_by[0]!.name
                        : `${t.used_by[0]!.name} and ${t.used_by.length - 1} more`}
                  </TableCell>
                  <TableCell>
                    <Badge tone={t.active ? "success" : "neutral"} size="sm">
                      {t.active ? "On" : "Off"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-text-muted" title={formatDateTime(t.updated_at)}>
                    {relativeTime(t.updated_at)}
                    {t.updated_by_name && <span className="block">by {t.updated_by_name}</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Page>
  );
}
